import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom"; // Adjust depending on router
import { API_BASE, downloadQuotation } from "../services/api";
import { showErrorToast, showSuccessToast } from "../services/toast";

export default function QuotationPreviewPage() {
  const { selectionId } = useParams();
  const navigate = useNavigate();

  const [quotationData, setQuotationData] = useState(null);
  const [fetching, setFetching] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [error, setError] = useState(null);

  // Fetch the quotation details when the page mounts
  useEffect(() => {
    const fetchQuotationData = async () => {
      try {
        setFetching(true);
        // Replace with your actual endpoint to fetch selection/quotation by ID
        const res = await fetch(`${API_BASE}/selection/${selectionId}`, {
          credentials: "include",
        });

        if (!res.ok) throw new Error("Failed to load quotation details.");
        
        const data = await res.json();
        setQuotationData(data);
      } catch (err) {
        console.error("Error fetching preview:", err);
        setError(err.message);
      } finally {
        setFetching(false);
      }
    };

    if (selectionId) {
      fetchQuotationData();
    }
  }, [selectionId]);

  const handleDownload = async () => {
    try {
      setDownloading(true);
      await downloadQuotation(selectionId);
      showSuccessToast("Quotation downloaded successfully!");
    } catch (err) {
      console.error("Download Error:", err);
      showErrorToast(err.message || "Failed to download quotation");
    } finally {
      setDownloading(false);
    }
  };

  if (fetching) {
    return (
      <div className="flex justify-center items-center h-64 text-sm text-gray-500">
        Loading quotation preview...
      </div>
    );
  }

  if (error || !quotationData) {
    return (
      <div className="p-6 text-center text-red-500">
        {error || "Quotation not found."}
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6 bg-white rounded-xl shadow-md border border-gray-100 mt-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-200">
        <div>
          <button
            onClick={() => navigate(-1)}
            className="text-xs text-gray-500 hover:text-gray-700 mb-2 underline"
          >
            ← Back to Editing
          </button>
          <h1 className="text-xl font-bold text-gray-800">Quotation Preview</h1>
          <p className="text-xs text-gray-500">Selection ID: {selectionId}</p>
        </div>

        {/* Download Action */}
        <button
          onClick={handleDownload}
          disabled={downloading}
          className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-xs font-semibold flex items-center gap-2 transition-all disabled:opacity-50"
        >
          <svg
            className="w-4 h-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
            />
          </svg>
          {downloading ? "Downloading..." : "Download Quotation PDF"}
        </button>
      </div>

      {/* Summary Info */}
      <div className="grid grid-cols-2 gap-4 mb-6 bg-gray-50 p-4 rounded-lg text-xs">
        <div>
          <span className="font-semibold text-gray-600">Wastage Percentage:</span>{" "}
          {quotationData?.wastage_percentage ?? 0}%
        </div>
        <div>
          <span className="font-semibold text-gray-600">Total Items:</span>{" "}
          {quotationData?.items?.length || 0}
        </div>
      </div>

      {/* Items Preview Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="bg-gray-100 text-gray-700">
              <th className="p-3 border-b">Item ID</th>
              <th className="p-3 border-b">Input Type</th>
              <th className="p-3 border-b">Input Value</th>
              <th className="p-3 border-b">Calculated Sq Ft</th>
            </tr>
          </thead>
          <tbody>
            {quotationData?.items?.map((item, idx) => (
              <tr key={item.id || idx} className="border-b hover:bg-gray-50">
                <td className="p-3 font-medium text-gray-800">{item.id}</td>
                <td className="p-3 uppercase">{item.input_type || "sq_ft"}</td>
                <td className="p-3">{item.input_value ?? item.area_sqft ?? 0}</td>
                <td className="p-3">{item.area_sqft || item.input_value || 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}